import {useState} from 'react'

import {useMedia} from 'react-use'
import sanitizeHtml from 'sanitize-html'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'

import {Modal, Tabs} from '@festi/common/components/common'
import {ProductDetail} from '@festi/common/api/rest'
import {styledTheme} from '@festi/common/themes'
import {fluidRange} from '@festi/common/utils/styles'
import {Property, cleanProperties} from '@festi/common/utils/products'

import ProductRichText from './NewProductRichText'
import {ViewMoreWrapper} from 'src/components/common'
import ProductPropertiesTable from './NewProductPropertiesTable'

const PROPERTIES_LIMIT = 5

const InfoTabContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 98px;

  ${media.lg`
    gap: 152px;
  `}
`

const ShowOnlyInMobile = styled.div`
  display: block;

  ${media.md`
    display: none;
  `}
`

const ShowOnlyInDesktop = styled.div`
  display: none;

  ${media.md`
    display: block;
  `}
`

const SellingPointsRoot = styled.div`
  padding-block: 24px;

  ${media.md`
    padding-top: 20px;
    padding-bottom: 40px;
  `}
`

const SellingPointTitle = styled.h5`
  font-size: 20px;
  font-weight: 500;
  ${fluidRange('padding-bottom', '20px', '28px')};
`

const SellingPointsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
`

const SellingPoint = styled.div`
  position: relative;
  width: 100%;
  color: ${styledTheme.palette.ui80Solid};
  padding-left: 15px;

  &:before {
    content: '';
    position: absolute;
    top: calc(50% - 2.5px);
    left: 0;
    display: block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    border: 1px solid ${styledTheme.palette.ui80Solid};
  }

  a {
    color: ${styledTheme.palette.link};
    cursor: pointer;
  }

  ${media.md`
    width: calc(50% - 12px);
  `}
`

const limit = 6

export function SellingPoints({sellingPoints}: {sellingPoints: string[]}) {
  if (!sellingPoints?.length) return null

  return (
    <SellingPointsRoot>
      <SellingPointTitle>Helstu atriði</SellingPointTitle>
      <SellingPointsList>
        {sellingPoints?.slice(0, limit).map((point, index) => (
          <SellingPoint
            key={index}
            dangerouslySetInnerHTML={{__html: sanitizeHtml(point)}}
          />
        ))}
      </SellingPointsList>
    </SellingPointsRoot>
  )
}

interface ProductAttributesProps {
  properties: Property[]
  product: ProductDetail
  showProductPropertiesModal: boolean
  setShowProductPropertiesModal: React.Dispatch<React.SetStateAction<boolean>>
  scrollToTab: number | null
  setScrollToTab: React.Dispatch<React.SetStateAction<number | null>>
}

export default function ProductAttributes({
  properties,
  product,
  showProductPropertiesModal,
  scrollToTab,
  setScrollToTab,
  setShowProductPropertiesModal,
}: ProductAttributesProps): JSX.Element {
  const [showProductDescriptionModal, setShowProductDescriptionModal] =
    useState<boolean>(false)

  const isMobile = useMedia(
    `(max-width: ${styledTheme.screenSize.sizeMD})`,
    false,
  )

  const {description, shortDescription, sellingPoints} = product

  const cleanedList = cleanProperties(properties)

  const cleanDescription = sanitizeHtml(description, {
    allowedTags: [],
    allowedAttributes: {},
  })
  const hideLongDescription =
    shortDescription?.length >= cleanDescription?.length

  const productAttributesTabs = [
    ...(!!description
      ? [
          {
            label: 'Upplýsingar',
            content: (
              <InfoTabContentWrapper>
                <ProductRichText html={description} />
                <div>
                  <SellingPoints sellingPoints={sellingPoints} />
                  {!!properties?.length && (
                    <ViewMoreWrapper
                      title="Eiginleikar"
                      hideShowMore={
                        isMobile && cleanedList?.length <= PROPERTIES_LIMIT
                      }
                      setViewMore={() => setScrollToTab(1)}
                    >
                      <ProductPropertiesTable
                        items={properties}
                        itemsToShow={PROPERTIES_LIMIT}
                        isCompact
                      />
                    </ViewMoreWrapper>
                  )}
                </div>
              </InfoTabContentWrapper>
            ),
          },
        ]
      : []),
    ...(!!properties?.length
      ? [
          {
            label: 'Eiginleikar',
            content: <ProductPropertiesTable items={properties} />,
          },
        ]
      : []),
  ]
  return (
    <>
      <ShowOnlyInDesktop>
        <Tabs
          tabs={productAttributesTabs}
          scrollToTab={scrollToTab}
          setScrollToTab={setScrollToTab}
        />
      </ShowOnlyInDesktop>

      <ShowOnlyInMobile>
        <SellingPoints sellingPoints={sellingPoints} />

        <ViewMoreWrapper
          title="Upplýsingar"
          hideShowMore={hideLongDescription}
          setViewMore={setShowProductDescriptionModal}
        >
          <ProductRichText html={shortDescription} />
        </ViewMoreWrapper>

        {!!properties?.length && (
          <ViewMoreWrapper
            title="Eiginleikar"
            hideShowMore={cleanedList?.length <= PROPERTIES_LIMIT}
            setViewMore={setShowProductPropertiesModal}
          >
            <ProductPropertiesTable
              items={properties}
              itemsToShow={PROPERTIES_LIMIT}
              isCompact
            />
          </ViewMoreWrapper>
        )}
      </ShowOnlyInMobile>

      {/* TODO: Change to new modal when ready */}
      <Modal
        title="Eiginleikar"
        isOpen={showProductPropertiesModal}
        onRequestClose={() => setShowProductPropertiesModal(false)}
        bottomMobile
      >
        <ProductPropertiesTable items={properties} />
      </Modal>

      <Modal
        title="Upplýsingar"
        isOpen={showProductDescriptionModal}
        onRequestClose={() => setShowProductDescriptionModal(false)}
        bottomMobile
      >
        <ProductRichText html={description} />
      </Modal>
    </>
  )
}
